import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SalesplanType } from '@app/sales-plan/models/sales-plan.model';
import { SalesPlanEventBlockViewService } from '@app/sales-plan/services/sales-plan-event-blockview.service';
import { SalesPlanFactoryService } from '@app/sales-plan/services/sales-plan-factory.service';
import { SalesPlanTopviewService } from '@app/sales-plan/services/sales-plan-topview.service';
import { SalesBlockViewData } from '@core/models/sales-plan.model';
import { AlertService } from '@core/services/alert.service';
import {
  EventSessionPlanBlockViewDetailsResponseDto,
  EventSessionPlanFilterRequestDto,
  EventSessionPlanResponseDto,
  OperationResultOfBasketSessionSeatStatusResponseDto,
  RebookingSeatSelectionRequestDto,
  RebookingSeatSelectionSuccessResponseDto,
  SalesPlanBlockFilterRequestDto,
  SalesPlanBlockFilterResponseDto,
  SalesPlanFiltersPriceResponseDto,
  SeatsBlockGroupResponseDto,
  SubscriptionDetailResponseDto,
} from '@core/services/apiclient';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import * as fromSales from '@root-store/sales-store/index';
import { SalesEventTypeEnum } from '@sales/models/sales.model';
import { SalesSubscriptionService } from '@sales/services/sales-subscription.service';
import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as SalesPlanActions from '../actions/sales-plan.actions';
import { SalesResubscriptionService } from '@sales/services/resubscription/sales-resubscription.service';

/**
 * Repli quand la reponse ne porte pas d'errorCode exploitable : erreur reseau, corps illisible, ou
 * un endpoint du forkJoin qui ne declare aucune erreur metier.
 */
const TOP_VIEW_FALLBACK_MESSAGE_KEY = 'salesPlan.PlanNotFound';

@Injectable()
export class SalesPlanEffects {
  private readonly actions = inject(Actions);
  private readonly router = inject(Router);
  private readonly alertService = inject(AlertService);
  private readonly planService = inject(SalesPlanFactoryService);
  private readonly subscriptionService = inject(SalesSubscriptionService);
  private readonly salesPlanStore = inject<Store<fromSalesPlan.State>>(Store);
  private readonly salesStore = inject<Store<fromSales.State>>(Store);
  private readonly eventBlockViewService = inject(SalesPlanEventBlockViewService);
  private readonly translate = inject(TranslateService);
  private readonly topViewService = inject(SalesPlanTopviewService);

  private readonly salesResubscriptionService = inject(SalesResubscriptionService);

  resetPlan$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromSales.actions.setChannel),
      distinctUntilChanged((prev, curr) => prev?.channelId === curr?.channelId),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSeatSelection)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventSeatSelection)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
      ),
      concatMap(([{ channelId }, selection, eventSelection, eventType]) => {
        const selectionData = eventType === SalesEventTypeEnum.Subscription ? selection : eventSelection;
        return [
          // resetting plan has two options
          // if we have a current selection: dispatch release selection and call the api to deflag the seats
          // else we reset the plan data directly
          selectionData?.length
            ? SalesPlanActions.releaseSelection({ withResetPlan: !channelId })
            : SalesPlanActions.resetPlan(),
        ];
      }),
    );
  });

  releaseSelection$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.releaseSelection),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSeatSelection)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventSeatSelection)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
      ),
      concatMap(([{ withResetPlan }, selection, eventSelection, eventType]) => {
        if (eventType === SalesEventTypeEnum.Subscription) {
          return [SalesPlanActions.updateSeatSelection({ selection, withResetPlan })];
        } else {
          return [SalesPlanActions.updateEventSeatSelection({ selection: eventSelection, withResetPlan })];
        }
      }),
    );
  });

  initPlanView$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.initPlanView),
      withLatestFrom(this.salesStore.pipe(select(fromSales.selectors.selectBasketId))),
      tap(([{ eventType }, basketId]) => {
        this.planService.useFactory(eventType);
      }),
      concatMap(([{ eventType, sessionId }, basketId]) => {
        return eventType === SalesEventTypeEnum.Subscription
          ? [
              SalesPlanActions.setBasketId({ basketId }),
              SalesPlanActions.loadSubscriptionDetails({ subscriptionId: sessionId }),
              SalesPlanActions.loadPlanData({}),
            ]
          : [SalesPlanActions.setBasketId({ basketId }), SalesPlanActions.loadPlanData({})];
      }),
      tap(() => {
        this.router.navigate(['sales', 'sales-plan'], {
          skipLocationChange: true,
        });
      }),
    );
  });

  reloadPlanView$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.reloadPlanView),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanType)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectBlockId)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
      ),
      switchMap(([action, planType, blockId, eventType]) => {
        if (eventType === SalesEventTypeEnum.Subscription) {
          return [SalesPlanActions.loadPlanData({ blockId: blockId })];
        } else if (planType === SalesplanType.MULTIBLOCK && !!blockId) {
          return [SalesPlanActions.refreshSalesBlockViewData({ blockId })];
        } else if (planType === SalesplanType.MULTIBLOCK && !blockId) {
          return [SalesPlanActions.loadSalesPlanTopView()];
        } else {
          return [SalesPlanActions.refreshSalesExcludingMultiBlocksViewSeatsData()];
        }
      }),
    );
  });

  refreshSalesBlockViewSeatsData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.refreshSalesBlockViewData),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectChannelId)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSessionId)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectBlockId)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanSeatsStampVersion)),
      ),
      switchMap(([action, channelId, sessionId, blockId, stampVersion]) => {
        return this.planService
          .getDifferentialBlockViewSeats(channelId, sessionId, blockId, stampVersion)
          .pipe(
            concatMap((seats) => {
              if (seats?.seats?.length) {
                this.eventBlockViewService.refreshBlockViewSeats(seats.seats);
                return [
                  SalesPlanActions.refreshSalesBlockViewSeatsSuccess({
                    seatsStampVersion: seats.stampVersion,
                  }),
                ];
              } else {
                return [{ type: 'Empty Action' }];
              }
            }),
            catchError(() => of(SalesPlanActions.refreshSalesBlockViewSeatsFailure())),
          );
      }),
    );
  });

  refreshSalesExcludingMultiBlocksViewSeatsData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.refreshSalesExcludingMultiBlocksViewSeatsData),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectChannelId)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSessionId)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanSeatsStampVersion)),
      ),
      switchMap(([action, channelId, sessionId, stampVersion]) => {
        return this.planService
          .getDifferentialSalesExcludingMultiBlocksSeats(channelId, sessionId, stampVersion)
          .pipe(
            concatMap((seats) => {
              if (seats?.seats?.length) {
                this.eventBlockViewService.refreshBlockViewSeats(seats.seats);
                return [
                  SalesPlanActions.refreshSalesExcludingMultiBlocksSeatsSuccess({
                    seatsStampVersion: seats.stampVersion,
                  }),
                ];
              } else {
                return [{ type: 'Empty Action' }];
              }
            }),
            catchError(() => of(SalesPlanActions.refreshSalesExcludingMultiBlocksSeatsFailure())),
          );
      }),
    );
  });

  loadPlanData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.loadPlanData),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanType)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectBlockId)),
      ),
      switchMap(([action, planType, blockId]) => {
        if (planType === SalesplanType.MULTIBLOCK && !!blockId) {
          return [SalesPlanActions.loadSalesBlockViewData({ blockId })];
        } else if (planType === SalesplanType.MULTIBLOCK && !blockId) {
          return [SalesPlanActions.loadSalesPlanTopView()];
        } else {
          return [SalesPlanActions.loadSalesExcludingMultiBlocksPlan()];
        }
      }),
    );
  });

  loadSalesPlanTopView$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.loadSalesPlanTopView),
      withLatestFrom(this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanTypeAndFilters))),
      switchMap(([action, { filters, channelId, sessionId, searchTerm, eventType }]) => {
        const filtersWithChannel: EventSessionPlanFilterRequestDto = { ...filters, channelId };

        const getEventBlocks$ =
          eventType === SalesEventTypeEnum.Event
            ? this.planService.getTopviewPlanOnlyBlocks(sessionId, channelId)
            : of(null);

        const getSubscriptionPlanData$ =
          eventType === SalesEventTypeEnum.Subscription
            ? this.planService.getTopviewPlanBlocks(sessionId, null, filtersWithChannel)
            : of(null);

        const getPlanBackground$ = this.planService.getPlanBackground(sessionId);

        return forkJoin([getSubscriptionPlanData$, getEventBlocks$, getPlanBackground$]).pipe(
          map(([subscriptionPlanData, eventBlocks, planBackground]) => ({
            planData: subscriptionPlanData,
            topViewBlocks:
              eventType === SalesEventTypeEnum.Event ? eventBlocks : subscriptionPlanData?.blocks,
            planBackground,
          })),
          concatMap(({ planData: subscriptionPlanData, topViewBlocks, planBackground }) => [
            SalesPlanActions.loadSalesTopViewSuccess({
              planBackground,
              planData: subscriptionPlanData,
              topViewBlocks,
            }),
            SalesPlanActions.getSeatSearchResults({ searchTerm }),
            SalesPlanActions.loadContingentsFilterData(),
            SalesPlanActions.loadCategoriesFilterData(),
            SalesPlanActions.loadPricesFilterData(),
          ]),
          catchError((error: unknown) => {
            // Le toast part d'ici, pas du composant : un effect traite une action une fois, la ou
            // `isEmpty$` est consomme par deux `| async` et emettrait le message en double.
            // `showApiError` resout `errors.<errorCode>`, surface le traceId, et se tait au-dela de
            // 500 — ces reponses-la sont deja toastees par ErrorInterceptor.
            this.alertService.showApiError(error, {
              fallback: TOP_VIEW_FALLBACK_MESSAGE_KEY,
              type: 'warning',
            });

            // Le libelle inline de la zone du plan lit cette meme cle depuis le store.
            return of(
              SalesPlanActions.loadSalesTopViewFailure({
                topViewErrorMessageKey: this.resolveTopViewErrorMessageKey(error),
              }),
            );
          }),
        );
      }),
    );
  });

  /**
   * Cle i18n du libelle affiche dans la zone du plan, resolue depuis l'errorCode du backend.
   *
   * L'existence de la cle est verifiee ici : le libelle inline affiche la valeur telle quelle via
   * `| translate`, et l'application ne declare aucun `MissingTranslationHandler` — ngx-translate
   * renvoie donc la cle elle-meme quand elle manque, ce qui reafficherait a l'utilisateur le defaut
   * que ce flux corrige. Le toast, lui, est deja protege par `resolveMessage` dans `AlertService`.
   *
   * Le `forkJoin` du chargement couvre trois endpoints : un code d'erreur peut donc venir du fond de
   * plan ou de la branche Abonnement, dont les erreurs de validation n'ont pas de libelle declare.
   */
  private resolveTopViewErrorMessageKey(error: unknown): string {
    const errorCode = this.alertService.getErrorCode(error);
    if (!errorCode) {
      return TOP_VIEW_FALLBACK_MESSAGE_KEY;
    }

    const errorKey = `errors.${errorCode}`;
    return this.translate.instant(errorKey) === errorKey ? TOP_VIEW_FALLBACK_MESSAGE_KEY : errorKey;
  }

  loadSalesBlockViewData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.loadSalesBlockViewData),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanTypeAndFilters)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectOtherRebookingSeats)),
      ),
      switchMap(([{ blockId }, planContext, otherRebookingSeats]) =>
        this.loadBlockData(blockId, planContext, otherRebookingSeats).pipe(
          concatMap(({ planData, blockData, seatsViewData, seatsStampVersion }) => [
            SalesPlanActions.loadSalesBlockViewSuccess({
              planData,
              blockData,
              seatsViewData,
              seatsStampVersion,
            }),
            SalesPlanActions.getSeatSearchResults({ searchTerm: planContext.searchTerm }),
            SalesPlanActions.loadContingentsFilterData(),
            SalesPlanActions.loadCategoriesFilterData(),
            SalesPlanActions.loadPricesFilterData(),
          ]),
          catchError(() => of(SalesPlanActions.loadSalesBlockViewFailure())),
        ),
      ),
    );
  });

  private loadBlockData(
    blockId: number,
    context: {
      filters: any;
      channelId: number;
      sessionId: number;
      searchTerm: string;
      eventType: SalesEventTypeEnum;
    },
    otherRebookingSeats: any[],
  ): Observable<{
    blockData: EventSessionPlanBlockViewDetailsResponseDto;
    planData: EventSessionPlanResponseDto;
    seatsViewData: SalesBlockViewData;
    seatsStampVersion: string;
  }> {
    const { filters, channelId, sessionId, eventType } = context;
    const filtersWithChannel: EventSessionPlanFilterRequestDto = { ...filters, channelId };

    return eventType === SalesEventTypeEnum.Subscription
      ? this.loadSubscriptionBlockData(sessionId, blockId, filtersWithChannel, otherRebookingSeats)
      : this.loadEventBlockData(sessionId, blockId, channelId);
  }

  private loadSubscriptionBlockData(
    sessionId: number,
    blockId: number,
    filters: EventSessionPlanFilterRequestDto,
    otherRebookingSeats: any[],
  ): Observable<{
    blockData: EventSessionPlanBlockViewDetailsResponseDto;
    planData: EventSessionPlanResponseDto;
    seatsViewData: null;
    seatsStampVersion: null;
  }> {
    const seatsInCurrentBlock = otherRebookingSeats.filter((seat) => seat?.blockId === blockId);

    return forkJoin([
      this.planService.getBlockData(sessionId, blockId, filters),
      this.planService.getTopviewPlanBlocks(sessionId, blockId, filters),
    ]).pipe(
      map(([blockData, planData]) => {
        if (seatsInCurrentBlock?.length && blockData?.seats?.length) {
          blockData.seats = blockData.seats.map((seat) =>
            seatsInCurrentBlock.some((s) => s.id === seat.id) ? { ...seat, isAvailable: false } : seat,
          );
        }
        return {
          blockData,
          planData,
          seatsViewData: null,
          seatsStampVersion: null,
        };
      }),
    );
  }

  private loadEventBlockData(
    sessionId: number,
    blockId: number,
    channelId: number,
  ): Observable<{
    blockData: null;
    planData: null;
    seatsViewData: SalesBlockViewData;
    seatsStampVersion: string;
  }> {
    return forkJoin([
      this.planService.getBlockViewSeats(channelId, sessionId, blockId),
      this.planService.getSalesBlockViewContingentsImages(sessionId, blockId),
      this.planService.getSalesBlockViewLetters(sessionId, blockId),
      this.planService.getSalesBlockViewImages(sessionId, blockId),
      this.planService.getSalesBlockViewShapes(sessionId, blockId),
    ]).pipe(
      map(([seats, contingents, lettersResponse, imageData, shapesResponse]) => ({
        planData: null,
        blockData: null,
        seatsViewData: {
          blockId,
          seats: seats.seats,
          letters: lettersResponse,
          contingents,
          shapes: shapesResponse,
          imageData,
        },
        seatsStampVersion: seats.stampVersion,
      })),
    );
  }

  loadExcludingMultiBlocksPlan$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.loadSalesExcludingMultiBlocksPlan),
      withLatestFrom(this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanTypeAndFilters))),
      switchMap(([action, { filters, channelId, sessionId, searchTerm, eventType }]) => {
        const filtersWithChannel: EventSessionPlanFilterRequestDto = { ...filters, channelId };
        let loadData$: Observable<{
          blockData: EventSessionPlanBlockViewDetailsResponseDto;
          planData: EventSessionPlanResponseDto;
          seatsViewData: SalesBlockViewData;
          seatsStampVersion: string;
        }>;
        if (eventType === SalesEventTypeEnum.Event) {
          loadData$ = combineLatest([
            this.planService.getSalesExcludingMultiBlocksSeats(channelId, sessionId),
            this.planService.getSalesExcludingMultiBlocksContingentsImages(sessionId),
            this.planService.getSalesExcludingMultiBlocksLetters(sessionId),
            this.planService.getSalesExcludingMultiBlocksImages(sessionId),
            this.planService.getSalesExcludingMultiBlocksShapes(sessionId),
          ]).pipe(
            map(([seats, contingentsResponse, lettersResponse, imageData, shapesResponse]) => ({
              blockData: null,
              planData: null,
              seatsViewData: {
                blockId: null,
                seats: seats.seats,
                letters: lettersResponse,
                contingents: contingentsResponse,
                shapes: shapesResponse,
                imageData: imageData,
              },
              seatsStampVersion: seats.stampVersion,
            })),
          );
        } else {
          forkJoin([
            this.planService.getBlockData(sessionId, null, filtersWithChannel),
            this.planService.getNumberedPlanFilter(sessionId, filtersWithChannel),
          ]).pipe(
            map(([blockData, planData]) => ({
              planData: { ...planData, zones: [] },
              blockData,
              seatsViewData: null,
              seatsStampVersion: null,
            })),
          );
        }
        return loadData$.pipe(
          concatMap(({ planData, blockData, seatsViewData, seatsStampVersion }) => {
            return [
              SalesPlanActions.loadSalesBlockViewSuccess({
                planData,
                blockData,
                seatsViewData,
                seatsStampVersion,
              }),
              SalesPlanActions.getSeatSearchResults({ searchTerm }),
              SalesPlanActions.loadContingentsFilterData(),
              SalesPlanActions.loadCategoriesFilterData(),
              SalesPlanActions.loadPricesFilterData(),
            ];
          }),
          catchError(() => of(SalesPlanActions.loadSalesBlockViewFailure())),
        );
      }),
    );
  });

  setFilters$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.setPlanFilters),
      withLatestFrom(this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanIds))),
      concatMap(([{ filters }, { blockId }]) => [SalesPlanActions.loadPlanData({ blockId: blockId })]),
    );
  });

  loadSubscriptionDetails$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.loadSubscriptionDetails),
      switchMap(({ subscriptionId }) =>
        this.subscriptionService.getSubscriptionDetails(subscriptionId).pipe(
          concatMap((details: SubscriptionDetailResponseDto) => [
            SalesPlanActions.loadSubscriptionDetailsSuccess({ details }),
          ]),
          catchError(() => of(SalesPlanActions.loadSubscriptionDetailsFailure())),
        ),
      ),
    );
  });

  getSearchResults$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.getSeatSearchResults),
      withLatestFrom(this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanIds))),
      switchMap(([{ searchTerm }, { sessionId, blockId }]) => {
        // sending zone null, to always get all zones search results and display icons on the minimap
        // even when in a zoneview
        const result$ = searchTerm
          ? this.planService.getSearchResults(sessionId, null, searchTerm || null)
          : of([]);
        return result$.pipe(
          concatMap((results: SeatsBlockGroupResponseDto[]) => {
            if (searchTerm) {
              const seatCount = results.reduce((acc, curr) => acc + curr?.seats?.length, 0);
              const alertPanelClass = document.fullscreenElement
                ? 'search-result-alert-fs'
                : 'search-result-alert-nofs';
              if (seatCount > 0) {
                this.alertService.showCustom(
                  this.translate.instant('sales.info.resultOfResearch', { number: seatCount }),
                  'success',
                  2000,
                  alertPanelClass,
                );
              } else {
                this.alertService.showCustom(
                  this.translate.instant('sales.info.noResultsAvailable'),
                  'info',
                  2000,
                  alertPanelClass,
                );
              }
            }
            return [SalesPlanActions.getSeatSearchResultsSuccess({ results })];
          }),
          catchError(() => of(SalesPlanActions.getSeatSearchResultsFailure())),
        );
      }),
    );
  });

  updateSeatSelection$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.updateSeatSelection),
      withLatestFrom(this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanIds))),
      mergeMap(([{ selection, withResetPlan }, { sessionId, basketId }]) => {
        const seatIds = selection
          .map(({ id }) => id)
          .filter((sel, index, self) => self.indexOf(sel) === index);
        return this.planService.flagSeats(seatIds, sessionId, basketId).pipe(
          concatMap((value: OperationResultOfBasketSessionSeatStatusResponseDto) => [
            fromSalesPlan.actions.updateSeatSelectionSuccess({
              selection,
              response: value?.model,
            }),
            // withResetPlan means that channelId changed to null, means that we need to set plan basketId to null
            // and reset plan ONLY when current selection has been released
            fromSalesPlan.actions.setBasketId({ basketId: withResetPlan ? null : value?.model?.basketId }),
            withResetPlan ? fromSalesPlan.actions.resetPlan() : { type: 'Empty Action' },
          ]),
          catchError((error: unknown) =>
            of(fromSalesPlan.actions.updateSeatSelectionFailure({ seatIds, withResetPlan, error })),
          ),
        );
      }),
    );
  });

  updateEventSeatSelection$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.updateEventSeatSelection),
      withLatestFrom(this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanIds))),
      mergeMap(([{ selection, withResetPlan }, { sessionId, basketId }]) => {
        const seatIds = selection
          .map(({ physicalSeatId }) => physicalSeatId)
          .filter((sel, index, self) => self.indexOf(sel) === index);
        return this.planService.flagSeats(seatIds, sessionId, basketId).pipe(
          concatMap((value: OperationResultOfBasketSessionSeatStatusResponseDto) => [
            fromSalesPlan.actions.updateEventSeatSelectionSuccess({
              selection,
              response: value?.model,
            }),
            // withResetPlan means that channelId changed to null, means that we need to set plan basketId to null
            // and reset plan ONLY when current selection has been released
            fromSalesPlan.actions.setBasketId({ basketId: withResetPlan ? null : value?.model?.basketId }),
            withResetPlan ? fromSalesPlan.actions.resetPlan() : { type: 'Empty Action' },
          ]),
          catchError((error: unknown) => {
            return of(fromSalesPlan.actions.updateEventSeatSelectionFailure({ seatIds, withResetPlan, error }));
          }),
        );
      }),
    );
  });

  updateResubscriptionSeatSelection$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.updateResubSeatSelection),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanIds)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectResubBeneficiarySeat)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.SelectResubSeatSelection)),
      ),
      mergeMap(
        ([{ selection }, { sessionId, basketId }, beneficiaryCurrentResubSeat, resubscriptionSelection]) => {
          return this.salesResubscriptionService
            .selectResubSeatAsync(basketId, sessionId, {
              defaultSeatId: beneficiaryCurrentResubSeat?.id,
              previousSelectedSeatId: resubscriptionSelection?.id
                ? resubscriptionSelection?.id
                : beneficiaryCurrentResubSeat?.id,
              newSelectedSeatId: selection?.id,
            } as RebookingSeatSelectionRequestDto)
            .pipe(
              concatMap((value: RebookingSeatSelectionSuccessResponseDto) => [
                fromSalesPlan.actions.updateResubSeatSelectionSuccess({
                  selection,
                }),
              ]),
              catchError((err: unknown) => {
                this.alertService.showApiError(
                  err,
                  this.translate.instant('salesPlan.info.placeCouldNotBeUpdated', { seatIds: selection?.id }),
                );
                return [{ type: 'Empty Action' }];
              }),
            );
        },
      ),
    );
  });

  updateSeatSelectionFailure$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.updateSeatSelectionFailure, SalesPlanActions.updateEventSeatSelectionFailure),
      tap(({ seatIds, error }) => {
        this.alertService.showApiError(error, {
          fallback: this.translate.instant(
            seatIds?.length === 1
              ? 'salesPlan.info.placeCouldNotBeUpdated'
              : 'salesPlan.info.placesCouldNotBeUpdated',
            { seatIds: seatIds.join(' - ') },
          ),
        });
      }),
      concatMap(({ withResetPlan }) =>
        withResetPlan
          ? [fromSalesPlan.actions.setBasketId({ basketId: null }), fromSalesPlan.actions.resetPlan()]
          : [{ type: 'Empty Action' }],
      ),
    );
  });

  flagUnnumberedSeats$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.flagUnnumberedSeats),
      withLatestFrom(this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanIds))),
      switchMap(([{ sessionSeats }, { sessionId, basketId }]) =>
        this.planService.flagUnnumberedSeats(sessionId, basketId, sessionSeats).pipe(
          concatMap((response: OperationResultOfBasketSessionSeatStatusResponseDto) => [
            fromSalesPlan.actions.flagUnnumberedSeatsSuccess({ response: response?.model, sessionSeats }),
            fromSalesPlan.actions.setBasketId({ basketId: response?.model?.basketId }),
          ]),
          catchError((error: unknown) =>
            of(fromSalesPlan.actions.flagUnnumberedSeatsFailure({ sessionSeats, error })),
          ),
        ),
      ),
    );
  });

  flagUnnumberedSeatsFailure$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(SalesPlanActions.flagUnnumberedSeatsFailure),
        tap(({ sessionSeats, error }) => {
          const quantity = sessionSeats?.reduce((acc, curr) => acc + curr?.quantity, 0);
          this.alertService.showApiError(
            error,
            `salesPlan.info.${
              quantity <= 1 ? 'unnumberedPlaceCouldNotBeFlagged' : 'unnumberedPlacesCouldNotBeFlagged'
            }`,
          );
        }),
      );
    },
    { dispatch: false },
  );

  applySeatPrices$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.applySeatPrices),
      switchMap(({ basketId, sessionId, priceApplyings, priceMappingFormHistory }) =>
        this.planService.applyPrices(basketId, sessionId, priceApplyings).pipe(
          concatMap((applyPricesResult) => [
            SalesPlanActions.applySeatPricesSuccess({ priceMappingFormHistory }),
          ]),
          catchError((error: unknown) => of(SalesPlanActions.applySeatPricesFailure({ error }))),
        ),
      ),
    );
  });

  applySeatPricesSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.applySeatPricesSuccess),
      tap(() => {
        this.alertService.showCustom('salesPlan.info.placesAddedSuccessfully', 'success');
      }),
      concatMap(() => [
        fromSales.actions.loadBasketInfo({}),
        fromSales.actions.pushBooking(),
        fromSales.actions.updateStepState('bookingsStep', { isValidated: true }),
        fromSalesPlan.actions.resetPlan(),
      ]),
    );
  });

  applySeatPricesFailure$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(SalesPlanActions.applySeatPricesFailure),
        tap(({ error }) => {
          this.alertService.showApiError(error, 'salesPlan.info.pricesCouldNotBeApplied');
        }),
      );
    },
    { dispatch: false },
  );

  removeBooking$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromSales.actions.removeBooking),
      concatMap(({ sessionId }) => [fromSalesPlan.actions.removeBookingSelectionHistory({ sessionId })]),
    );
  });

  loadContingentsFilterData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        SalesPlanActions.loadContingentsFilterData,
        SalesPlanActions.updateCategoryIdsFilterSelected,
        SalesPlanActions.updatePriceIdsFilterSelected,
        SalesPlanActions.resetSalesPlanFilters,
      ),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSalesPlanSelectedFilter)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectBlockId)),
      ),
      switchMap(([action, filter, eventType, blockId]) => {
        if (eventType === SalesEventTypeEnum.Event) {
          return this.planService
            .getContingentsList(
              filter.sessionId,
              filter.channelId,
              filter.categoriesFilter,
              filter.pricesFilter,
              blockId,
            )
            .pipe(
              concatMap((data) => [
                SalesPlanActions.updateContingentsFilterList({
                  contingentsFilterList: data,
                }),
              ]),
              catchError(() => of(null)),
            );
        }
        return [{ type: 'Empty Action' }];
      }),
    );
  });

  loadCategoriesFilterData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        SalesPlanActions.loadCategoriesFilterData,
        SalesPlanActions.updateContingentIdsFilterSelected,
        SalesPlanActions.updatePriceIdsFilterSelected,
        SalesPlanActions.resetSalesPlanFilters,
      ),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSalesPlanSelectedFilter)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectBlockId)),
      ),
      exhaustMap(([action, filter, eventType, blockId]) => {
        if (eventType === SalesEventTypeEnum.Event) {
          return this.planService
            .getCategoriesList(
              filter.sessionId,
              filter.channelId,
              filter.contingentsFilter,
              filter.pricesFilter,
              blockId,
            )
            .pipe(
              concatMap((data) => [
                SalesPlanActions.updateCategoriesFilterList({
                  categoriesFilterList: data,
                  fromTopView: !blockId,
                }),
              ]),
              catchError(() => of(null)),
            );
        }
        return [{ type: 'Empty Action' }];
      }),
    );
  });

  loadPricesFilterData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        SalesPlanActions.loadPricesFilterData,
        SalesPlanActions.updateContingentIdsFilterSelected,
        SalesPlanActions.updateCategoryIdsFilterSelected,
        SalesPlanActions.resetSalesPlanFilters,
      ),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSalesPlanSelectedFilter)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectBlockId)),
      ),
      exhaustMap(([action, filter, eventType, blockId]) => {
        if (eventType === SalesEventTypeEnum.Event) {
          return this.planService
            .getPricesList(
              filter.sessionId,
              filter.channelId,
              filter.contingentsFilter,
              filter.categoriesFilter,
              blockId,
            )
            .pipe(
              concatMap((data: SalesPlanFiltersPriceResponseDto[]) => {
                return [
                  SalesPlanActions.updatePricesFilterList({
                    pricesFilterList: data,
                    fromTopView: !blockId,
                  }),
                ];
              }),
              catchError(() => of(null)),
            );
        }
        return [{ type: 'Empty Action' }];
      }),
    );
  });

  applyPlanFilter$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.applyPlanFilter),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectPlanType)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectBlockId)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSalesPlanSelectedFilter)),
      ),
      switchMap(([action, planType, eventType, blockId, selectedFilters]) => {
        if (eventType === SalesEventTypeEnum.Event) {
          if (planType === SalesplanType.MULTIBLOCK && !blockId) {
            const filters: SalesPlanBlockFilterRequestDto = {
              contingentIds: selectedFilters.contingentsFilter,
              categoryIds: selectedFilters.categoriesFilter,
              priceIds: selectedFilters.pricesFilter,
            };
            return this.planService
              .applyTopviewFilter(selectedFilters.channelId, selectedFilters.sessionId, filters)
              .pipe(
                concatMap((data: SalesPlanBlockFilterResponseDto[]) => {
                  this.topViewService.refreshBlockAvailabilities(data);
                  return [{ type: 'Empty Action' }];
                }),
                catchError(() => of({ type: 'EMPTY' })),
              );
          }
        }
        return [{ type: 'Empty Action' }];
      }),
    );
  });

  loadPlanDataSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SalesPlanActions.loadSalesTopViewSuccess),
      withLatestFrom(
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectSalesPlanSelectedFilter)),
        this.salesPlanStore.pipe(select(fromSalesPlan.selectors.selectEventType)),
      ),
      switchMap(([action, filter, eventType]) => {
        if (
          eventType === SalesEventTypeEnum.Event &&
          (filter.contingentsFilter?.length > 0 ||
            filter.categoriesFilter?.length > 0 ||
            filter.pricesFilter?.length > 0)
        ) {
          const filters: SalesPlanBlockFilterRequestDto = {
            contingentIds: filter.contingentsFilter,
            categoryIds: filter.categoriesFilter,
            priceIds: filter.pricesFilter,
          };
          return this.planService.applyTopviewFilter(filter.channelId, filter.sessionId, filters).pipe(
            concatMap((filteredBlocks: SalesPlanBlockFilterResponseDto[]) => {
              const planBlockData = action.topViewBlocks.map((blockData) => {
                const filteredBlock = filteredBlocks.find(
                  (currentFilteredBlock) => currentFilteredBlock.id == blockData.id,
                );
                return filteredBlock
                  ? { ...blockData, availableSeats: filteredBlock.availableSeats }
                  : { ...blockData, availableSeats: 0 };
              });
              return [
                SalesPlanActions.reloadPlanFilterDataSuccess({
                  topViewBlocks: planBlockData,
                }),
              ];
            }),
            catchError(() => of(null)),
          );
        }
        return [{ type: 'Empty Action' }];
      }),
    );
  });
}
